import { QueryKey } from '@tanstack/query-core';

import { http } from './helper/fetch';
import {
    buildGetRequest,
    buildPostRequest,
    parseJsonResponse,
} from './helper/json';
import { buildRpcRequest, parseRpcResponse } from './helper/rpc';
import { buildTimeoutAbortController } from './timeout';
import {
    ApiCallOptions,
    ContentType,
    EndpointConfig,
    EndpointDefinition,
    EndpointType,
    FetchError,
    GetEndpointRequestType,
    GetEndpointResponseType,
    GetEndpointUrlParamsType,
    HttpResponse,
    RequestBuilder,
    ResponseParser,
    TypeMapType,
} from './types';

export function getApiEndpointConfig<
    TypeMap extends TypeMapType,
    Endpoint extends keyof TypeMap,
    TError = unknown,
    TQueryKey extends QueryKey = QueryKey
>(config: EndpointConfig<TypeMap>, endpoint: Endpoint) {
    return config[endpoint] as EndpointDefinition<
        TypeMap,
        Endpoint,
        TError,
        TQueryKey
    >;
}

/**
 * returns the RequestBuilder and ResponseParser implementations based on the endpoint config
 */
async function getImplementations<
    TypeMap extends TypeMapType,
    Endpoint extends keyof TypeMap
>(
    endpoint: Endpoint,
    endpointConfig: EndpointDefinition<TypeMap, Endpoint>,
    rpcContentType: ContentType
): Promise<
    [
        RequestBuilder<
            GetEndpointRequestType<TypeMap, Endpoint>,
            GetEndpointUrlParamsType<TypeMap, Endpoint>
        >,
        ResponseParser<GetEndpointResponseType<TypeMap, Endpoint>>
    ]
> {
    switch (endpointConfig.type) {
        case EndpointType.Get:
            return [buildGetRequest, parseJsonResponse];
        case EndpointType.Post:
            return [buildPostRequest, parseJsonResponse];
        case EndpointType.Rpc:
            const converter = await endpointConfig.converterProvider();
            return [
                (options) =>
                    buildRpcRequest(converter.Request, rpcContentType, options),
                (response) =>
                    parseRpcResponse(
                        endpoint,
                        converter.Response,
                        rpcContentType,
                        response
                    ),
            ];
    }
}

async function prepareUrl<
    TypeMap extends TypeMapType,
    Endpoint extends keyof TypeMap,
    TError = unknown,
    TQueryKey extends QueryKey = QueryKey
>(
    url: string | URL | undefined,
    endpointConfig: EndpointDefinition<TypeMap, Endpoint, TError, TQueryKey>
) {
    if (url) {
        return url;
    }
    if (!endpointConfig.baseUrlProvider) {
        return endpointConfig.url;
    }
    const baseUrl = await endpointConfig.baseUrlProvider();

    return `${baseUrl}${endpointConfig.url}`;
}

/**
 * builds the AbortController used by the underlying fetch depending on given AbortController and timeout setting
 */
function buildInternalAbortController(
    externalAbortController: AbortController | undefined,
    timeout: number | undefined
): [AbortController | undefined, () => void] {
    const [timeoutAbortController, clearTimeout] =
        buildTimeoutAbortController(timeout);
    if (!externalAbortController) {
        // no external AbortController -> just return the timeout AbortController, if present
        return [timeoutAbortController, clearTimeout];
    }
    if (!timeoutAbortController) {
        // no timeout AbortController -> just return the timeout AbortController, if present
        return [externalAbortController, clearTimeout];
    }
    // both, external and timeout AbortController present -> abort timeout AbortController on external abort
    externalAbortController.signal.addEventListener('abort', () => {
        timeoutAbortController.abort(externalAbortController.signal.reason);
    });

    return [timeoutAbortController, clearTimeout];
}

export async function callApiEndpoint<
    TypeMap extends TypeMapType,
    Endpoint extends keyof TypeMap
>(
    config: EndpointConfig<TypeMap>,
    rpcContentType: ContentType,
    endpoint: Endpoint,
    {
        url,
        requestOptions = {},
        requestData,
        queryParams,
        urlParams,
        abortController,
        timeout,
    }: ApiCallOptions<TypeMap, Endpoint>
): Promise<HttpResponse<GetEndpointResponseType<TypeMap, Endpoint>>> {
    const endpointConfig = getApiEndpointConfig(config, endpoint);
    url = await prepareUrl(url, endpointConfig);
    if (!url) {
        throw new FetchError(
            `Api URL not found for endpoint: ${String(endpoint)}`
        );
    }
    const [requestBuilder, responseParser] = await getImplementations(
        endpoint,
        endpointConfig,
        rpcContentType
    );
    const [internalAbortController, clearTimeout] =
        buildInternalAbortController(
            abortController,
            timeout ?? endpointConfig.timeout
        );
    if (internalAbortController) {
        requestOptions = {
            ...requestOptions,
            signal: internalAbortController.signal,
        };
    }

    try {
        return await http(
            {
                url,
                requestOptions,
                requestData,
                queryParams,
                urlParams,
            },
            requestBuilder,
            responseParser
        );
    } catch (e) {
        if (
            internalAbortController &&
            (e as Record<string, unknown>).name === 'AbortError'
        ) {
            // fetch aborted -> just simulate response with status code 500 and attach the signal
            return {
                ...new Response(null, {
                    status: 500,
                }),
                signal: internalAbortController.signal,
            };
        }
        throw e;
    } finally {
        // fetch finished -> clear timeout, if necessary
        clearTimeout();
    }
}
